import { FullSingleQuote } from "@/types";
import {
  generateQuotePDFAsBase64,
  generateQuotePDFAsBlob,
} from "@/utils/pdfGenerator.util";
import { useCallback, useState } from "react";

/**
 * Custom hook for handling PDF generation and viewing
 * @returns Object containing PDF generation functions and state
 */
export const usePDFGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generates PDF as base64 string for use with ViewPDF component
   * @param quote - The quote data to generate PDF from
   * @returns Promise resolving to base64 string
   */
  const generatePDFAsBase64 = useCallback(
    async (quote: FullSingleQuote): Promise<string | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const base64String = await generateQuotePDFAsBase64(quote);
        return base64String;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate PDF";
        setError(errorMessage);
        console.error("PDF generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Generates PDF as blob for direct download
   * @param quote - The quote data to generate PDF from
   * @returns Promise resolving to Blob
   */
  const generatePDFAsBlob = useCallback(
    async (quote: FullSingleQuote): Promise<Blob | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const blob = await generateQuotePDFAsBlob(quote);
        return blob;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate PDF";
        setError(errorMessage);
        console.error("PDF generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Downloads PDF directly to user's device
   * @param quote - The quote data to generate PDF from
   * @param filename - Optional filename for download (defaults to quote number)
   */
  const downloadPDF = useCallback(
    async (quote: FullSingleQuote, filename?: string): Promise<void> => {
      const blob = await generatePDFAsBlob(quote);

      if (!blob) {
        // Error handling is already done in generatePDFAsBlob
        return;
      }

      try {
        // Create download link and trigger download
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || `quote-${quote.quote_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        const errorMessage = "Failed to download PDF";
        setError(errorMessage);
        console.error("PDF download error:", err);
      }
    },
    [generatePDFAsBlob]
  );

  /**
   * Clears any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    generatePDFAsBase64,
    generatePDFAsBlob,
    downloadPDF,
    isGenerating,
    error,
    clearError,
  };
};
